/* Per-path telemetry series shared by the screens that draw them. */

// ── WAN down-interval overlay (from wan_down / wan_up events) ────────────────
//
// When a WAN link drops (e.g. cable unplugged), the edge stops emitting
// wan_link samples for it entirely — so its loss_ratio series has a *gap*,
// not a run of 100%-loss points. A naive heat-strip paints that gap "no data"
// (gray) when it should read "down" (red): the WAN was genuinely unreachable,
// we just have no probe samples because the interface is gone.
//
// The authoritative signal is the wan_down → wan_up event pair the edge
// records. buildWanDownIntervals pairs them per WAN into [start, end) ranges
// (end = Infinity while still down) so the strips can paint those slots red
// regardless of sample gaps.
//
// Not every wan_down is an outage. A phone tags a network it no longer has
// (it left the Wi-Fi, or the radio was turned off) cause "gone": that link is
// absent, not down, so its gap stays "no data" instead of reading as a fault.
export function buildWanDownIntervals(events) {
  const byWan = new Map();
  const evs = (events || [])
    .filter((e) => e.type === "wan_up" || (e.type === "wan_down" && e.tags?.cause !== "gone"))
    .map((e) => ({
      wan: e.tags?.wan,
      down: e.type === "wan_down",
      t: new Date(e.ts).getTime(),
    }))
    .filter((e) => e.wan && isFinite(e.t))
    .sort((a, b) => a.t - b.t);
  const openDown = new Map();
  for (const e of evs) {
    if (e.down) {
      if (!openDown.has(e.wan)) openDown.set(e.wan, e.t);
    } else if (openDown.has(e.wan)) {
      const start = openDown.get(e.wan);
      openDown.delete(e.wan);
      if (!byWan.has(e.wan)) byWan.set(e.wan, []);
      byWan.get(e.wan).push({ start, end: e.t });
    }
  }
  for (const [wan, start] of openDown) {
    if (!byWan.has(wan)) byWan.set(wan, []);
    byWan.get(wan).push({ start, end: Infinity });
  }
  return byWan;
}
